"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SHORTCUT_EVENTS } from "@/lib/shortcuts";
import { eventBus } from "@/services-frontend/comment/eventBus";

interface SearchContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

/**
 * Provides global open/close state for the search modal.
 *
 * Mounted once at the top of the provider tree in app/providers.tsx. The
 * SearchModal subscribes to `isOpen`; anything with a "search" button
 * (sidebar, command palette, etc.) calls `open()`.
 *
 * ⌘K itself is no longer handled here. It lives in the shortcut registry
 * (lib/shortcuts/bindings/global.ts) and arrives as an event, so that every
 * shortcut in the app is declared and checked for conflicts in one place.
 */
export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  useEffect(() => {
    const handler = () => setIsOpen((v) => !v);
    eventBus.on(SHORTCUT_EVENTS.toggleSearch, handler);
    return () => eventBus.off(SHORTCUT_EVENTS.toggleSearch, handler);
  }, []);

  const value = useMemo<SearchContextValue>(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

export function useSearchModal(): SearchContextValue {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    // Return an inert no-op context so components can safely call open()
    // even if they're rendered above the provider (e.g. in a test).
    return {
      isOpen: false,
      open: () => undefined,
      close: () => undefined,
      toggle: () => undefined,
    };
  }
  return ctx;
}
